import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { auth, db, functions } from "@/lib/firebase";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import type { LgsApplication } from "@/types/lgsApplication";
import type { LGSPrefs } from "@/types/user";
import { buildLgsSearchUrl } from "@/lib/lgs";
import { onAuthStateChanged, User } from "firebase/auth";

/** Small helpers */
function trimOrUndef(s: string) {
  const t = (s || "").trim();
  return t ? t : undefined;
}
function normalizeUrl(raw: string) {
  const t = (raw || "").trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}
function validUrlOrEmpty(raw: string) {
  const t = (raw || "").trim();
  if (!t) return "";
  try {
    // eslint-disable-next-line no-new
    new URL(normalizeUrl(t));
    return normalizeUrl(t);
  } catch {
    return t; // let UI show it, we gate on submit
  }
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function LgsUpgradeModal({ open, onOpenChange }: Props) {
  /** Auth: never rely on auth.currentUser directly */
  const [user, setUser] = useState<User | null>(auth.currentUser);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, setUser);
    return () => unsub();
  }, []);

  /** Claims & verification state */
  const [claimsLoaded, setClaimsLoaded] = useState(false);
  const [isLGSClaim, setIsLGSClaim] = useState<boolean>(false);
  const emailVerified = !!user?.emailVerified;

  /** Form state */
  const [businessName, setBusinessName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [listingsUrl, setListingsUrl] = useState("");
  const [websiteSearchTemplate, setWebsiteSearchTemplate] = useState("");
  const [publicEmailMode, setPublicEmailMode] =
    useState<"account" | "custom">("account");
  const [publicEmail, setPublicEmail] = useState("");

  /** UX state */
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<"draft" | "submit" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  /** Load custom claims on open (and when user changes) */
  useEffect(() => {
    let mounted = true;
    (async () => {
      setClaimsLoaded(false);
      try {
        if (!user) {
          if (mounted) {
            setIsLGSClaim(false);
            setClaimsLoaded(true);
          }
          return;
        }
        const tok = await user.getIdTokenResult(true);
        if (!mounted) return;
        setIsLGSClaim(Boolean((tok.claims as any).isLGS));
      } catch {
        if (!mounted) return;
        setIsLGSClaim(false);
      } finally {
        if (mounted) setClaimsLoaded(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [user?.uid, open]);

  /** Prefill from existing application each time the modal opens */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError(null);
      setOkMsg(null);
      if (!open || !user) return;
      setLoading(true);
      try {
        const ref = doc(db, "lgs_applications", user.uid);
        const snap = await getDoc(ref);
        if (cancelled) return;
        if (snap.exists()) {
          const d = snap.data() as LgsApplication;
          setBusinessName(d.businessName || "");
          setWebsiteUrl(d.websiteUrl || "");
          setListingsUrl(d.listingsUrl || "");
          setWebsiteSearchTemplate(d.websiteSearchTemplate || "");
          setPublicEmailMode((d.publicEmailMode as any) || "account");
          setPublicEmail(d.publicEmail || "");
        } else {
          setBusinessName("");
          setWebsiteUrl("");
          setListingsUrl("");
          setWebsiteSearchTemplate("");
          setPublicEmailMode("account");
          setPublicEmail("");
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Failed to load application.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, user?.uid]);

  /** Live preview link for Lightning Bolt */
  const previewUrl = useMemo(() => {
    const prefs: LGSPrefs = {
      contactEmailMode: publicEmailMode,
      contactEmail:
        publicEmailMode === "custom" ? trimOrUndef(publicEmail) : undefined,
      websiteUrl: normalizeUrl(websiteUrl) || "",
      listingsUrl: trimOrUndef(listingsUrl),
      websiteSearchTemplate: trimOrUndef(websiteSearchTemplate),
      offerContact: "website",
    };
    try {
      if (!prefs.websiteUrl && !prefs.listingsUrl) return "";
      return buildLgsSearchUrl(prefs, "Lightning Bolt");
    } catch {
      return "";
    }
  }, [
    websiteUrl,
    listingsUrl,
    websiteSearchTemplate,
    publicEmailMode,
    publicEmail,
  ]);

  /** Validation */
  function validate(): string | null {
    if (!user?.uid) return "You must be signed in.";
    if (!emailVerified) return "Please verify your email before submitting.";
    if (isLGSClaim) return "Your account is already LGS-verified.";

    if (!businessName.trim()) return "Business name is required.";
    if (!websiteUrl.trim()) return "Website URL is required.";

    // shallow URL validation on submit
    try {
      // throws if invalid
      // eslint-disable-next-line no-new
      new URL(normalizeUrl(websiteUrl));
      if (listingsUrl.trim()) new URL(normalizeUrl(listingsUrl));
    } catch {
      return "Please enter a valid Website/Listings URL (include domain only).";
    }

    if (publicEmailMode === "custom" && !publicEmail.trim())
      return "Public email is required when using custom mode.";

    // Optional {q} hint – not an error
    return null;
  }

  /** Build payload (Firestore timestamps, not Date.now) */
  const uid = user?.uid || "";
  function basePayload(): LgsApplication {
    return {
      uid,
      businessName: businessName.trim(),
      websiteUrl: normalizeUrl(websiteUrl),
      listingsUrl: trimOrUndef(validUrlOrEmpty(listingsUrl)),
      websiteSearchTemplate: trimOrUndef(websiteSearchTemplate),
      publicEmailMode,
      publicEmail:
        publicEmailMode === "custom" ? publicEmail.trim() : undefined,
      status: "draft",
      // stored as epoch ms in your original type; keep both stamp + ms for flexibility
      createdAt: Date.now(),
      updatedAt: Date.now(),
      createdAtTS: Timestamp.now(),
      updatedAtTS: Timestamp.now(),
    } as any;
  }

  async function writeApplication(status: LgsApplication["status"]) {
    const err = validate();
    if (err) {
      setError(err);
      return false;
    }
    try {
      setError(null);
      const ref = doc(db, "lgs_applications", uid);
      const snap = await getDoc(ref);

      const payload = { ...basePayload(), status } as any;
      if (snap.exists()) {
        // preserve first-created
        const prev = snap.data() as any;
        payload.createdAt = prev.createdAt || payload.createdAt;
        payload.createdAtTS = prev.createdAtTS || payload.createdAtTS;
      }
      // also keep server timestamps so your admin tools can sort consistently
      payload.createdAtServer = snap.exists()
        ? prevOrServer(prevTimestamp(prev))
        : serverTimestamp();
      payload.updatedAtServer = serverTimestamp();

      await setDoc(ref, payload, { merge: true });
      return true;
    } catch (e: any) {
      setError(e?.message || "Failed to save application.");
      return false;
    }

    function prevTimestamp(prev: any) {
      return prev?.updatedAtServer || prev?.createdAtServer || null;
    }
    function prevOrServer(ts: any) {
      return ts || serverTimestamp();
    }
  }

  const handleSaveDraft = async () => {
    if (!uid) return;
    setSaving("draft");
    const ok = await writeApplication("draft");
    setSaving(null);
    if (ok) setOkMsg("Draft saved.");
  };

  const handleSubmit = async () => {
    if (!uid) return;
    setSaving("submit");
    const ok = await writeApplication("submitted");
    if (ok) {
      try {
        const runChecks = httpsCallable(functions, "runLgsAutoChecks");
        await runChecks({ uid });
        setOkMsg("Submitted for review. Checks are running.");
      } catch {
        setOkMsg("Submitted for review.");
      }
    }
    setSaving(null);
  };

  /** Render states — never hide the modal, show the reason instead */
  const showVerifyBanner = claimsLoaded && user && !emailVerified;
  const showAlreadyLgs = claimsLoaded && isLGSClaim;
  const submitDisabled =
    !user ||
    !claimsLoaded ||
    !emailVerified ||
    isLGSClaim ||
    loading ||
    saving !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Upgrade to Verified LGS</DialogTitle>
          <DialogDescription>
            Provide your shop details. Save a draft or submit for review when
            ready.
          </DialogDescription>
        </DialogHeader>

        {/* Top banners */}
        {!claimsLoaded ? (
          <div className="py-3 text-sm text-muted-foreground">Checking your account…</div>
        ) : null}

        {showVerifyBanner ? (
          <div className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-200 text-sm">
            Please verify your email address to submit an application.
          </div>
        ) : null}

        {showAlreadyLgs ? (
          <div className="mb-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-emerald-200 text-sm">
            Your account is already LGS-verified. 🎉
          </div>
        ) : null}

        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Loading…
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
            className="space-y-4"
          >
            {error ? (
              <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-200 text-sm">
                {error}
              </div>
            ) : null}
            {okMsg ? (
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-emerald-200 text-sm">
                {okMsg}
              </div>
            ) : null}

            <div className="grid gap-2">
              <label className="text-sm font-medium">Business Name</label>
              <input
                type="text"
                className="w-full rounded-md border border-slate-700 bg-transparent px-3 py-2 text-sm"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                required
                placeholder="Ex: Arcane Emporium"
              />
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium">Website URL</label>
              <input
                type="url"
                className="w-full rounded-md border border-slate-700 bg-transparent px-3 py-2 text-sm"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                required
                placeholder="https://yourshop.com"
              />
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium">Listings URL (optional)</label>
              <input
                type="url"
                className="w-full rounded-md border border-slate-700 bg-transparent px-3 py-2 text-sm"
                value={listingsUrl}
                onChange={(e) => setListingsUrl(e.target.value)}
                placeholder="https://yourshop.com/marketplace"
              />
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium">Website Search Template (optional)</label>
              <input
                type="text"
                className="w-full rounded-md border border-slate-700 bg-transparent px-3 py-2 text-sm"
                value={websiteSearchTemplate}
                onChange={(e) => setWebsiteSearchTemplate(e.target.value)}
                placeholder='https://yourshop.com/search?q={q}'
              />
              <p className="text-[12px] text-slate-400">
                Use {"{q}"} as a placeholder for the card name.
              </p>
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium">Public Email</label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="public-email-mode"
                    className="accent-indigo-500"
                    checked={publicEmailMode === "account"}
                    onChange={() => setPublicEmailMode("account")}
                  />
                  Use account email ({user?.email || "unknown"})
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="public-email-mode"
                    className="accent-indigo-500"
                    checked={publicEmailMode === "custom"}
                    onChange={() => setPublicEmailMode("custom")}
                  />
                  Custom
                </label>
              </div>
              {publicEmailMode === "custom" && (
                <input
                  type="email"
                  className="mt-2 w-full rounded-md border border-slate-700 bg-transparent px-3 py-2 text-sm"
                  value={publicEmail}
                  onChange={(e) => setPublicEmail(e.target.value)}
                  placeholder="contact@yourshop.com"
                  required
                />
              )}
            </div>

            <div className="rounded-md border border-slate-700/50 bg-black/30 px-3 py-2 text-xs">
              <div className="font-medium text-slate-200 mb-1">
                Preview: Lightning Bolt
              </div>
              <div className="truncate text-slate-400">
                {previewUrl || "—"}
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={handleSaveDraft}
                disabled={saving !== null || !user}
              >
                {saving === "draft" ? "Saving…" : "Save Draft"}
              </Button>

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  disabled={saving !== null}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitDisabled}>
                  {saving === "submit" ? "Submitting…" : "Submit for Review"}
                </Button>
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

